import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Clock, Play, Pause, StopCircle, Coffee, ArrowRightLeft, Plus, FileText, Award, AlertTriangle, ChevronDown, ChevronRight, ChevronLeft, CalendarDays, Zap, CheckCircle2 } from 'lucide-react';
import TimeAllocation from '../../components/TimeAllocation';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { toast } from 'sonner';
import { fmtETTime, fmtETDate, todayISO_ET } from '../../utils/tz';
import { useAuth, formatApiErrorDetail } from '../../contexts/AuthContext';
import UnfinishedBillingModal from '../../components/UnfinishedBillingModal';
import BreakTimer from '../../components/BreakTimer';
import { publishClockState, readClockCache } from '../../hooks/useClockGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtTime = (iso) => iso ? fmtETTime(iso) : '—';

// Human-friendly hours/minutes ("8h 23m", "38m", "0m") from whole minutes.
const fmtHM = (mins) => {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
};

const PURE_OFFICE_ROLES = new Set(['office_staff', 'manager']);
const FIELD_TECH_ROLES = new Set(['technician', 'employee']);

const FALLBACK_REASONS = [
  { value: 'no_shift_scheduled', label: 'No shift scheduled' },
  { value: 'emergency_call',     label: 'Emergency call' },
  { value: 'forgot_to_assign',   label: 'Forgot to assign shift' },
  { value: 'office_admin_approved', label: 'Office / admin approved' },
  { value: 'other',              label: 'Other' },
];

// One clock/break value cell, readable on mobile.
const TimeCell = ({ label, value, sub, accent }) => (
  <div className="min-w-[84px]">
    <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">{label}</p>
    <p className={`text-base font-bold ${accent ? 'text-[#D4AF37]' : 'text-white'}`}>{value}</p>
    {sub ? <p className="text-[11px] text-[#737373]">{sub}</p> : null}
  </div>
);

// Collapsible lower-priority section wrapper (Today tab).
const Section = ({ id, title, icon: Icon, children, count }) => {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="bg-[#141414] border border-[#262626] rounded-md">
      <CollapsibleTrigger asChild>
        <button data-testid={`section-toggle-${id}`} className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left">
          <span className="flex items-center gap-2 text-white font-bold text-sm">
            {Icon && <Icon className="w-4 h-4 text-[#D4AF37]" />} {title}
            {count != null && <span className="text-[11px] text-[#737373] font-normal">({count})</span>}
          </span>
          {open ? <ChevronDown className="w-4 h-4 text-[#A3A3A3]" /> : <ChevronRight className="w-4 h-4 text-[#A3A3A3]" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4 pt-1">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const TimeClock = () => {
  const { user } = useAuth();

  const [tab, setTab] = useState('today');
  // clocked_in: null = not confirmed yet (never shown as "Clocked Out" while checking).
  const [status, setStatus] = useState(() => {
    const c = readClockCache(user?._id || user?.id);
    return c && c.required ? { clocked_in: c.clockedIn, _cached: true } : { clocked_in: null };
  });
  const today = todayISO_ET();
  const [sheet, setSheet] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Weekly tab
  const [weekAnchor, setWeekAnchor] = useState(today);
  const [week, setWeek] = useState(null);
  const [drawerDate, setDrawerDate] = useState(null);
  const [drawerSheet, setDrawerSheet] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerBreaks, setDrawerBreaks] = useState({});

  // Fallback clock-in dialog
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [fallbackForm, setFallbackForm] = useState({ reason: 'no_shift_scheduled', notes: '' });

  // Generic bonus dialog + after-hours bonus dialog
  const [bonusOpen, setBonusOpen] = useState(false);
  const [bonusForm, setBonusForm] = useState({ reason: '', proposed_amount: '', appointment_id: '' });
  const [bonuses, setBonuses] = useState([]);
  const [ahOpen, setAhOpen] = useState(false);
  const [ahCandidates, setAhCandidates] = useState([]);
  const [ahSelected, setAhSelected] = useState('');
  const [ahNotes, setAhNotes] = useState('');
  const [ahLoading, setAhLoading] = useState(false);

  // Time-edit / missing-entry dialogs
  const [editOpen, setEditOpen] = useState(false);
  const [unfinishedOpen, setUnfinishedOpen] = useState(false);
  const [unfinishedPrecheck, setUnfinishedPrecheck] = useState(null);
  const [editForm, setEditForm] = useState({ entry_id: '', proposed_clock_in: '', proposed_clock_out: '', reason: '', reason_category: '', correction_type: 'both' });
  const [missingOpen, setMissingOpen] = useState(false);
  const [missingForm, setMissingForm] = useState({ date: '', proposed_clock_in: '', proposed_clock_out: '', reason: '', reason_category: '' });
  const [myEdits, setMyEdits] = useState([]);
  const [openBreaks, setOpenBreaks] = useState({});

  const editByEntry = useMemo(() => {
    const map = {};
    for (const r of myEdits) {
      if (!r.entry_id) continue;
      const prev = map[r.entry_id];
      if (!prev || new Date(r.created_at || 0) > new Date(prev.created_at || 0)) map[r.entry_id] = r;
    }
    return map;
  }, [myEdits]);

  // Note + material
  const [noteText, setNoteText] = useState('');
  const [matForm, setMatForm] = useState({ item_name: '', quantity: '', unit: 'pcs', appointment_id: '', cost: '' });

  const refresh = useCallback(async () => {
    const statusP = axios.get(`${API}/timeclock/status`, { withCredentials: true })
      .then((r) => { setStatus(r.data); publishClockState(user?._id || user?.id, r.data); })
      .catch(() => { /* keep last confirmed status */ });
    try {
      const [t, a, b] = await Promise.all([
        axios.get(`${API}/timesheets/me?date=${today}`, { withCredentials: true }),
        axios.get(`${API}/schedule?start_date=${today}&end_date=${today}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/bonus-requests/me`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSheet(t.data);
      setAppointments(a.data);
      setBonuses(b.data);
    } catch (err) { console.error(err); }
    await statusP;
  }, [today, user?._id, user?.id]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refresh]);

  const loadWeek = useCallback(async (anchor) => {
    try {
      const { data } = await axios.get(`${API}/timesheets/me/week?week_start=${anchor}`, { withCredentials: true });
      setWeek(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { if (tab === 'weekly') loadWeek(weekAnchor); }, [tab, weekAnchor, loadWeek]);

  const shiftWeek = (deltaDays) => {
    const base = week?.week_start || weekAnchor;
    const d = new Date(`${base}T12:00:00`);
    d.setDate(d.getDate() + deltaDays);
    setWeekAnchor(d.toISOString().slice(0, 10));
  };

  const openDay = async (dateStr) => {
    setDrawerDate(dateStr);
    setDrawerSheet(null);
    setDrawerLoading(true);
    try {
      const { data } = await axios.get(`${API}/timesheets/me?date=${dateStr}`, { withCredentials: true });
      setDrawerSheet(data);
    } catch { toast.error('Failed to load day'); }
    setDrawerLoading(false);
  };

  const myUserId = user?.id || user?._id;
  const myAppointments = useMemo(() => {
    if (!myUserId) return [];
    return (appointments || []).filter((a) => {
      if (a.employee_id === myUserId) return true;
      const helpers = a.helpers || [];
      return helpers.some((h) => h?.employee_id === myUserId);
    });
  }, [appointments, myUserId]);

  const isFieldTech = !!user && (
    FIELD_TECH_ROLES.has(user.role) ||
    (!PURE_OFFICE_ROLES.has(user.role) && myAppointments.length > 0)
  );

  const clockIn = async (appointment_id = null, fallback_reason = null) => {
    setLoading(true);
    try {
      const body = { action: 'in', appointment_id };
      if (fallback_reason) body.fallback_reason = fallback_reason;
      await axios.post(`${API}/timeclock`, body, { withCredentials: true });
      toast.success(appointment_id ? 'Clocked into shift' : (fallback_reason ? 'Clocked in (fallback)' : 'Clocked in'));
      window.dispatchEvent(new Event('nexflow:clock-changed'));
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed'); }
    setLoading(false);
  };

  const submitFallback = async () => {
    if (!fallbackForm.reason) { toast.error('Pick a reason'); return; }
    await clockIn(null, fallbackForm.reason);
    setFallbackOpen(false);
    setFallbackForm({ reason: 'no_shift_scheduled', notes: '' });
  };

  const clockOut = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/timeclock/out-precheck`, { withCredentials: true });
      if (data?.requires_reason_if_no_finish && (data.unfinished_today || []).length > 0) {
        setUnfinishedPrecheck(data);
        setUnfinishedOpen(true);
        setLoading(false);
        return;
      }
      await axios.post(`${API}/timeclock`, { action: 'out' }, { withCredentials: true });
      toast.success('Clocked out');
      window.dispatchEvent(new Event('nexflow:clock-changed'));
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed'); }
    setLoading(false);
  };

  const onUnfinishedClockedOut = () => {
    toast.success('Clocked out — reason logged for admin');
    setUnfinishedOpen(false);
    setUnfinishedPrecheck(null);
    window.dispatchEvent(new Event('nexflow:clock-changed'));
    refresh();
  };

  const onUnfinishedFinishBilling = (firstJob) => {
    setUnfinishedOpen(false);
    if (firstJob?.id) {
      window.location.href = `/employee/schedule?finish_billing=${firstJob.id}`;
    } else {
      window.location.href = '/employee/schedule';
    }
  };

  const switchJob = async (appointment_id) => {
    setLoading(true);
    try {
      await axios.post(`${API}/timeclock/switch`, { appointment_id }, { withCredentials: true });
      toast.success(appointment_id ? 'Switched to job' : 'Back to general time');
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed'); }
    setLoading(false);
  };

  const toggleBreak = async () => {
    const url = status.on_break ? `${API}/timeclock/break/end` : `${API}/timeclock/break/start`;
    try {
      await axios.post(url, {}, { withCredentials: true });
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed'); }
  };

  const submitBonus = async () => {
    try {
      await axios.post(`${API}/bonus-requests`, {
        reason: bonusForm.reason,
        proposed_amount: bonusForm.proposed_amount ? Number(bonusForm.proposed_amount) : null,
        appointment_id: bonusForm.appointment_id || null,
      }, { withCredentials: true });
      toast.success('Bonus requested — pending approval');
      setBonusOpen(false);
      setBonusForm({ reason: '', proposed_amount: '', appointment_id: '' });
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed'); }
  };

  const openAfterHours = async () => {
    setAhOpen(true);
    setAhSelected('');
    setAhNotes('');
    try {
      const { data } = await axios.get(`${API}/bonus-requests/after-hours/candidates`, { withCredentials: true });
      setAhCandidates(data.candidates || []);
    } catch { setAhCandidates([]); }
  };

  const submitAfterHours = async () => {
    if (!ahSelected) { toast.error('Pick the completed job.'); return; }
    setAhLoading(true);
    try {
      const { data } = await axios.post(`${API}/bonus-requests/after-hours`, {
        appointment_id: ahSelected, notes: ahNotes || '',
      }, { withCredentials: true });
      toast.success(data.needs_office_review
        ? 'Submitted for office review — eligibility will be confirmed by the office.'
        : 'After-hours $100 bonus requested — pending approval.');
      setAhOpen(false);
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed'); }
    setAhLoading(false);
  };

  const submitEditRequest = async () => {
    const ctype = editForm.correction_type || 'both';
    const wantsIn = ctype !== 'clock_out_only';
    const wantsOut = ctype !== 'clock_in_only';
    if (wantsIn && !wantsOut && !editForm.proposed_clock_in) { toast.error('Enter the corrected clock-in time.'); return; }
    if (wantsOut && !wantsIn && !editForm.proposed_clock_out) { toast.error('Enter the corrected clock-out time.'); return; }
    if (wantsIn && wantsOut && !editForm.proposed_clock_in && !editForm.proposed_clock_out) { toast.error('Enter at least one time to correct.'); return; }
    try {
      const body = { entry_id: editForm.entry_id, reason: editForm.reason, reason_category: editForm.reason_category, correction_type: ctype };
      if (wantsIn && editForm.proposed_clock_in) body.proposed_clock_in = new Date(editForm.proposed_clock_in).toISOString();
      if (wantsOut && editForm.proposed_clock_out) body.proposed_clock_out = new Date(editForm.proposed_clock_out).toISOString();
      await axios.post(`${API}/time-edits`, body, { withCredentials: true });
      toast.success('Edit request submitted');
      setEditOpen(false);
      setEditForm({ entry_id: '', proposed_clock_in: '', proposed_clock_out: '', reason: '', reason_category: '', correction_type: 'both' });
      refreshMyEdits();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed'); }
  };

  const submitMissingRequest = async () => {
    if (!missingForm.date || !missingForm.proposed_clock_in || !missingForm.proposed_clock_out || !missingForm.reason.trim()) {
      toast.error('Fill date, clock-in, clock-out, and reason.');
      return;
    }
    try {
      await axios.post(`${API}/time-edits/missing`, {
        date: missingForm.date,
        proposed_clock_in: new Date(missingForm.proposed_clock_in).toISOString(),
        proposed_clock_out: new Date(missingForm.proposed_clock_out).toISOString(),
        reason: missingForm.reason,
        reason_category: missingForm.reason_category,
      }, { withCredentials: true });
      toast.success('Missing entry request submitted');
      setMissingOpen(false);
      setMissingForm({ date: '', proposed_clock_in: '', proposed_clock_out: '', reason: '', reason_category: '' });
      refreshMyEdits();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed'); }
  };

  const refreshMyEdits = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/time-edits/mine`, { withCredentials: true });
      setMyEdits(data || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { refreshMyEdits(); }, [refreshMyEdits]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/timesheets/me/note`, { note: noteText.trim(), date: today }, { withCredentials: true });
      toast.success('Note saved');
      setNoteText('');
      refresh();
    } catch { toast.error('Failed'); }
  };

  const addMaterial = async () => {
    if (!matForm.item_name || !matForm.quantity) return;
    try {
      await axios.post(`${API}/timesheets/me/material`, {
        item_name: matForm.item_name,
        quantity: Number(matForm.quantity),
        unit: matForm.unit,
        appointment_id: matForm.appointment_id || null,
        cost: matForm.cost ? Number(matForm.cost) : null,
        date: today,
      }, { withCredentials: true });
      toast.success('Material logged');
      setMatForm({ item_name: '', quantity: '', unit: 'pcs', appointment_id: '', cost: '' });
      refresh();
    } catch { toast.error('Failed'); }
  };

  const ahSelectedObj = ahCandidates.find((c) => c.appointment_id === ahSelected);

  // Reusable renderer for the entry cards (used on Today + in the Day drawer).
  const renderEntry = (e, breaksState, setBreaksState, allowEdit) => {
    const edit = allowEdit ? editByEntry[e.id] : null;
    const breaks = e.breaks_detail || [];
    const isOpen = !!breaksState[e.id];
    const completedBreakMin = breaks.filter((b) => !b.active).reduce((s, b) => s + (b.minutes || 0), 0);
    const editChip = edit && (
      <span data-testid={`entry-edit-status-${e.id}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded ${edit.status === 'approved' ? 'text-green-300 bg-green-500/15 border-green-500/40' : edit.status === 'rejected' ? 'text-red-300 bg-red-500/15 border-red-500/40' : 'text-amber-200 bg-amber-500/15 border-amber-500/40'}`}>
        Edit {edit.status}
      </span>
    );
    return (
      <div key={e.id} data-testid={`entry-card-${e.id}`} className={`bg-[#141414] border rounded-md p-4 ${e.fallback_clockin ? 'border-amber-500/30 bg-amber-500/5' : 'border-[#262626]'}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
            <TimeCell label="Clock In" value={fmtTime(e.clock_in)} />
            <TimeCell label="Clock Out" value={e.clock_out ? fmtTime(e.clock_out) : <span className="text-[#D4AF37]">On clock</span>} />
            <TimeCell label="Time on Clock" value={fmtHM(e.gross_minutes)} sub={`${(e.gross_hours ?? 0).toFixed(2)}h`} />
            <TimeCell label="Break Deducted" value={fmtHM(e.break_minutes)} sub={`${(e.break_hours ?? 0).toFixed(2)}h`} />
            <TimeCell label="Paid Time" value={fmtHM(e.paid_minutes)} sub={`${(e.paid_hours ?? 0).toFixed(2)}h`} accent />
          </div>
          {allowEdit && (
            <Button
              data-testid={`request-edit-${e.id}`}
              size="sm"
              variant="outline"
              className="border-[#262626] text-[#D4AF37] hover:bg-[#262626] shrink-0"
              onClick={() => { setEditForm({ entry_id: e.id, proposed_clock_in: '', proposed_clock_out: '', reason: '', reason_category: '', correction_type: e.clock_out ? 'both' : 'clock_in_only' }); setEditOpen(true); }}
            >
              Request Edit
            </Button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-[#D4D4D4]">{e.appointment_label || (e.fallback_clockin ? 'Fallback Time' : 'General Time')}</span>
          {e.fallback_clockin && (
            <span data-testid={`fallback-badge-${e.id}`} className="text-[10px] font-bold uppercase tracking-wider text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5">
              Fallback · {FALLBACK_REASONS.find(r => r.value === e.fallback_reason)?.label || e.fallback_reason || 'review'}
            </span>
          )}
          {editChip}
        </div>
        {e.notes && <p className="mt-2 text-xs text-[#A3A3A3]" data-testid={`entry-notes-${e.id}`}>Notes: {e.notes}</p>}

        <button
          type="button"
          data-testid={`toggle-breaks-${e.id}`}
          onClick={() => setBreaksState((o) => ({ ...o, [e.id]: !o[e.id] }))}
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#A3A3A3] hover:text-white"
        >
          {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          <Coffee className="w-3.5 h-3.5 text-[#D4AF37]" /> Breaks ({breaks.length})
        </button>
        {isOpen && (
          <div data-testid={`breaks-detail-${e.id}`} className="mt-2 pl-5 border-l border-[#262626] space-y-1">
            {breaks.length === 0 ? (
              <p className="text-xs text-[#737373]">No breaks recorded for this entry.</p>
            ) : (<>
              {breaks.map((b, i) => (
                <div key={i} className="text-xs text-[#D4D4D4]">
                  <span className="text-[#A3A3A3]">Break {i + 1}:</span>{' '}
                  {fmtTime(b.start)} → {b.active ? <span className="text-[#D4AF37]">break not yet ended</span> : fmtTime(b.end)}
                  {' · '}
                  {b.active ? <span className="text-[#D4AF37]">{fmtHM(b.minutes)} elapsed</span> : fmtHM(b.minutes)}
                </div>
              ))}
              <div className="text-xs text-white font-medium pt-1">Total break time: {fmtHM(completedBreakMin)}</div>
            </>)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="timeclock-page" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1">My Timesheet</h2>
        <p className="text-[#A3A3A3]">Clock in/out, breaks, weekly review & after-hours bonus</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="timesheet-tabs" className="bg-[#141414] border border-[#262626]">
          <TabsTrigger data-testid="tab-today" value="today" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white">Today</TabsTrigger>
          <TabsTrigger data-testid="tab-weekly" value="weekly" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white">Weekly</TabsTrigger>
        </TabsList>

        {/* ─────────────── TODAY ─────────────── */}
        <TabsContent value="today" className="space-y-6 mt-4">
          {/* Clock controls */}
          <div className="bg-[#141414] border border-[#262626] rounded-md p-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Status</p>
                {status.clocked_in == null ? (
                  <p data-testid="clock-status-checking" className="text-[#A3A3A3] text-2xl font-bold">Checking…</p>
                ) : status.clocked_in ? (
                  <div>
                    <p className="text-[#D4AF37] text-2xl font-bold flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      Clocked In
                    </p>
                    <p className="text-white text-sm mt-1">
                      {status._cached ? <span data-testid="clock-status-confirming" className="text-[#A3A3A3]">Confirming details…</span> : <>
                      {status.entry?.appointment_label ? `Shift: ${status.entry.appointment_label}` : (status.entry?.fallback_clockin ? 'Fallback Time (admin review)' : 'General Time')} • Since {fmtTime(status.entry?.clock_in)}
                      </>}
                    </p>
                    {status.entry?.fallback_clockin && (
                      <p data-testid="fallback-status-badge" className="text-amber-300 text-xs mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Fallback reason: {FALLBACK_REASONS.find(r => r.value === status.entry.fallback_reason)?.label || status.entry.fallback_reason}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-white text-2xl font-bold">Clocked Out</p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {status.clocked_in == null ? null : !status.clocked_in ? (
                  isFieldTech ? (
                    <Button data-testid="fallback-clock-in-btn" onClick={() => setFallbackOpen(true)} disabled={loading} variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
                      <Play className="w-4 h-4 mr-2" /> Clock In Without Shift
                    </Button>
                  ) : (
                    <Button data-testid="clock-in-btn" onClick={() => clockIn(null)} disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                      <Play className="w-4 h-4 mr-2" /> Clock In
                    </Button>
                  )
                ) : (
                  <>
                    <Button data-testid="toggle-break-btn" onClick={toggleBreak} disabled={loading} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">
                      {status.on_break ? <><Pause className="w-4 h-4 mr-2" /> End Break</> : <><Coffee className="w-4 h-4 mr-2" /> Start Break</>}
                    </Button>
                    <Button data-testid="clock-out-btn" onClick={clockOut} disabled={loading} variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white">
                      <StopCircle className="w-4 h-4 mr-2" /> Clock Out for Day
                    </Button>
                  </>
                )}
              </div>
            </div>

            {status.clocked_in && status.on_break && status.break_started_at && (
              <div className="mb-4">
                <BreakTimer breakStartedAt={status.break_started_at} alertMinutes={status.break_alert_minutes || 30} />
              </div>
            )}

            {isFieldTech && status.clocked_in === false && (
              <div data-testid="shift-first-hint" className="border-t border-[#262626] pt-3 pb-1">
                <p className="text-[#A3A3A3] text-sm">
                  <span className="text-[#D4AF37] font-bold">Pick today&apos;s shift below</span> to clock in. Use <span className="italic">Clock In Without Shift</span> only if you have no scheduled shift (a reason is required).
                </p>
              </div>
            )}

            <div className="border-t border-[#262626] pt-4">
              <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">Today&apos;s Scheduled Shifts</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {myAppointments.length === 0 ? (
                  <p data-testid="no-shifts-msg" className="text-[#A3A3A3] text-sm">No shifts scheduled today</p>
                ) : myAppointments.map((a) => {
                  const isCurrent = status.entry?.appointment_id === a.id;
                  const isAuto = !!a.auto_generated;
                  return (
                    <div
                      key={a.id}
                      data-testid={`shift-card-${a.id}`}
                      className={`border rounded-md p-3 ${isCurrent ? 'border-[#D4AF37] bg-[#D4AF37]/5' : (isAuto ? 'border-[#404040] bg-[#0A0A0A]' : 'border-[#262626] bg-[#0A0A0A]')}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-white text-sm font-bold truncate">{a.shift_title || a.service_type || 'Shift'}</p>
                          <p className="text-[#A3A3A3] text-xs">{a.customer_name || (isAuto ? 'Internal' : '—')} • {a.start_time}–{a.end_time}</p>
                        </div>
                        {isAuto && <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] bg-[#262626] px-1.5 py-0.5 rounded">Auto</span>}
                      </div>
                      {status.clocked_in == null ? null : !status.clocked_in ? (
                        <Button data-testid={`job-clock-in-${a.id}`} onClick={() => clockIn(a.id)} size="sm" className="mt-2 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] w-full">
                          <Play className="w-3 h-3 mr-1" /> Clock Into Shift
                        </Button>
                      ) : isCurrent ? (
                        <Button data-testid={`shift-current-${a.id}`} onClick={() => switchJob(null)} size="sm" variant="outline" className="mt-2 w-full border-[#D4AF37] text-[#D4AF37]">
                          <ArrowRightLeft className="w-3 h-3 mr-1" /> Complete Shift
                        </Button>
                      ) : (
                        <Button data-testid={`job-switch-${a.id}`} onClick={() => switchJob(a.id)} size="sm" variant="outline" className="mt-2 w-full border-[#262626] text-white hover:bg-[#262626]">
                          <ArrowRightLeft className="w-3 h-3 mr-1" /> Switch to This Shift
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Prominent After-Hours Bonus CTA */}
          <div data-testid="after-hours-cta" className="bg-gradient-to-r from-[#1a1508] to-[#141414] border border-[#D4AF37]/40 rounded-md p-5 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
                <Zap className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-white font-bold text-base">After-Hours Completed Call Bonus — <span className="text-[#D4AF37]">$100</span></p>
                <p className="text-[#A3A3A3] text-sm">Completed a weekend, emergency, or on-call job? Request your $100 bonus.</p>
              </div>
            </div>
            <Button data-testid="open-after-hours-bonus" onClick={openAfterHours} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
              <Award className="w-4 h-4 mr-2" /> Request After-Hours Bonus
            </Button>
          </div>

          {/* Where Their Hours Went */}
          {sheet?.allocation && <TimeAllocation allocation={sheet.allocation} />}

          {/* Daily Time Summary */}
          <div data-testid="daily-time-summary" className="bg-[#141414] border border-[#262626] rounded-md p-4">
            <h3 className="font-['Outfit'] text-sm font-bold text-white mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#D4AF37]" /> Today&apos;s Time Summary
              <span className="text-[10px] text-[#737373] font-normal normal-case">(America/New_York)</span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div data-testid="summary-clock-in">
                <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Clocked In</p>
                <p className="text-lg font-bold text-white">{fmtTime(sheet?.clocked_in)}</p>
              </div>
              <div data-testid="summary-clock-out">
                <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Clocked Out</p>
                <p className="text-lg font-bold text-white">{sheet?.has_open_entry ? <span className="text-[#D4AF37]">On clock</span> : fmtTime(sheet?.clocked_out)}</p>
              </div>
              <div data-testid="summary-gross">
                <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Total Time on Clock</p>
                <p className="text-lg font-bold text-white">{fmtHM(sheet?.gross_minutes)}</p>
                <p className="text-[11px] text-[#737373]">{(sheet?.gross_hours ?? 0).toFixed(2)}h</p>
              </div>
              <div data-testid="summary-break">
                <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Total Break Time</p>
                <p className="text-lg font-bold text-white">{fmtHM(sheet?.break_minutes)}</p>
                <p className="text-[11px] text-[#737373]">{(sheet?.break_hours ?? 0).toFixed(2)}h</p>
              </div>
              <div data-testid="summary-paid">
                <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Paid Time</p>
                <p className="text-lg font-bold text-[#D4AF37]">{fmtHM(sheet?.paid_minutes)}</p>
                <p className="text-[11px] text-[#737373]">{(sheet?.total_hours ?? 0).toFixed(2)} hours</p>
              </div>
            </div>
            <p className="text-[10px] text-[#737373] mt-3">Time on clock − breaks = paid time.</p>
          </div>

          {/* Today's Entries */}
          <div>
            <h3 className="font-['Outfit'] text-lg font-bold text-white flex items-center gap-2 mb-3"><Clock className="w-5 h-5 text-[#D4AF37]" /> Today&apos;s Entries</h3>
            <div className="space-y-3" data-testid="entries-list">
              {(sheet?.entries || []).length === 0 ? (
                <div className="text-center px-4 py-6 text-[#A3A3A3] bg-[#141414] border border-[#262626] rounded-md">No entries today yet</div>
              ) : sheet.entries.map((e) => renderEntry(e, openBreaks, setOpenBreaks, true))}
            </div>
          </div>

          {/* ── Collapsible lower-priority sections ── */}
          <div className="space-y-3">
            <Section id="edit-requests" title="View My Edit Requests" icon={FileText} count={myEdits.length}>
              <div className="flex justify-end mb-3">
                <Button
                  data-testid="request-missing-entry"
                  size="sm"
                  variant="outline"
                  onClick={() => { setMissingForm({ date: today, proposed_clock_in: '', proposed_clock_out: '', reason: '', reason_category: '' }); setMissingOpen(true); }}
                  className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                >
                  + Request Missing Entry
                </Button>
              </div>
              {myEdits.length === 0 ? (
                <p className="text-[#A3A3A3] text-sm">No edit requests yet.</p>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="my-edit-requests">
                  {myEdits.slice(0, 20).map((r) => {
                    const statusColor = r.status === 'approved' ? 'text-green-300 bg-green-500/15 border-green-500/40'
                      : r.status === 'rejected' ? 'text-red-300 bg-red-500/15 border-red-500/40'
                      : 'text-amber-200 bg-amber-500/15 border-amber-500/40';
                    return (
                      <div key={r.id} data-testid={`edit-request-${r.id}`} className="border border-[#262626] rounded p-2 bg-[#0d0d0d]">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded ${statusColor}`}>{r.status}</span>
                            <span className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">{r.request_type === 'missing' ? 'Missing entry' : 'Edit'}</span>
                            {r.date && <span className="text-[11px] text-white">{r.date}</span>}
                            <span className="text-[11px] text-[#D4D4D4] truncate max-w-[280px]" title={r.reason}>{r.reason}</span>
                          </div>
                          <span className="text-[10px] text-[#525252]">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</span>
                        </div>
                        {r.status === 'rejected' && r.decline_reason && (
                          <div className="mt-1 text-[11px] text-red-300 border-l-2 border-red-500/40 pl-2">Declined: {r.decline_reason}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>

            <Section id="notes" title="Notes" icon={FileText} count={(sheet?.notes || []).length}>
              <div className="flex gap-2 mb-3">
                <Input data-testid="day-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note about today..." className="bg-[#0A0A0A] border-[#262626] text-white" />
                <Button data-testid="day-note-submit" onClick={addNote} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Save</Button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(sheet?.notes || []).length === 0 ? <p className="text-[#A3A3A3] text-sm">No notes yet.</p> : (sheet?.notes || []).map((n) => (
                  <p key={n.id} className="text-[#A3A3A3] text-sm border-l-2 border-[#D4AF37] pl-2">{n.note} <span className="text-[#525252] text-xs">— {fmtTime(n.created_at)}</span></p>
                ))}
              </div>
            </Section>

            <Section id="materials" title="Materials Used" icon={Plus} count={(sheet?.materials || []).length}>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <Input data-testid="mat-name" value={matForm.item_name} onChange={(e) => setMatForm({...matForm, item_name: e.target.value})} placeholder="Item" className="bg-[#0A0A0A] border-[#262626] text-white" />
                <Input data-testid="mat-qty" type="number" value={matForm.quantity} onChange={(e) => setMatForm({...matForm, quantity: e.target.value})} placeholder="Qty" className="bg-[#0A0A0A] border-[#262626] text-white" />
                <Input value={matForm.unit} onChange={(e) => setMatForm({...matForm, unit: e.target.value})} placeholder="Unit" className="bg-[#0A0A0A] border-[#262626] text-white" />
                <Input type="number" value={matForm.cost} onChange={(e) => setMatForm({...matForm, cost: e.target.value})} placeholder="Cost $" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <Button data-testid="mat-submit" onClick={addMaterial} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] w-full mb-3"><Plus className="w-3 h-3 mr-1" /> Log Material</Button>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {(sheet?.materials || []).length === 0 ? <p className="text-[#A3A3A3] text-sm">No materials logged.</p> : (sheet?.materials || []).map((m) => (
                  <p key={m.id} className="text-[#A3A3A3] text-sm">• {m.item_name} ×{m.quantity} {m.unit} {m.cost ? `— $${Number(m.cost).toFixed(2)}` : ''}</p>
                ))}
              </div>
            </Section>

            <Section id="bonus-history" title="View Bonus Request History" icon={Award} count={bonuses.length}>
              <div className="flex justify-end mb-3">
                <Dialog open={bonusOpen} onOpenChange={setBonusOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="open-bonus-dialog" size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><Plus className="w-3 h-3 mr-1" /> Other Bonus Request</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#141414] border-[#262626] text-white">
                    <DialogHeader><DialogTitle className="text-[#D4AF37]">Request a Bonus</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-white">Job (optional)</Label>
                        <Select value={bonusForm.appointment_id} onValueChange={(v) => setBonusForm({...bonusForm, appointment_id: v})}>
                          <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="None" /></SelectTrigger>
                          <SelectContent className="bg-[#141414] border-[#262626]">
                            {appointments.map((a) => <SelectItem key={a.id} value={a.id} className="text-white">{a.shift_title || a.service_type} — {a.customer_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-white">Reason *</Label>
                        <Textarea data-testid="bonus-reason" value={bonusForm.reason} onChange={(e) => setBonusForm({...bonusForm, reason: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" rows={3} />
                      </div>
                      <div>
                        <Label className="text-white">Proposed Amount ($, optional)</Label>
                        <Input data-testid="bonus-amount" type="number" value={bonusForm.proposed_amount} onChange={(e) => setBonusForm({...bonusForm, proposed_amount: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="Leave blank if admin should set" />
                      </div>
                      <Button data-testid="bonus-submit" onClick={submitBonus} disabled={!bonusForm.reason} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] w-full">Submit Request</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-2">
                {bonuses.length === 0 ? (
                  <p className="text-[#A3A3A3] text-sm">No bonus requests yet</p>
                ) : bonuses.map((b) => (
                  <div key={b.id} data-testid={`bonus-row-${b.id}`} className="bg-[#0d0d0d] border border-[#262626] rounded-md p-3 flex items-center justify-between flex-wrap gap-2">
                    <div className="min-w-0">
                      <p className="text-white text-sm flex items-center gap-2">
                        {b.bonus_type === 'after_hours' && <span className="text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded px-1.5 py-0.5">After-Hours</span>}
                        <span className="truncate">{b.reason}</span>
                      </p>
                      <p className="text-[#525252] text-xs">{fmtETDate(b.created_at)} {b.proposed_amount ? `• $${Number(b.proposed_amount).toFixed(2)}` : ''}{b.needs_office_review ? ' • Needs office review' : ''}</p>
                    </div>
                    <span className={`text-xs uppercase font-bold px-2 py-1 rounded ${b.status === 'approved' ? 'text-green-400 bg-green-500/10' : b.status === 'rejected' ? 'text-red-400 bg-red-500/10' : 'text-[#D4AF37] bg-[#D4AF37]/10'}`}>
                      {b.status}{b.final_amount ? ` $${Number(b.final_amount).toFixed(2)}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </TabsContent>

        {/* ─────────────── WEEKLY ─────────────── */}
        <TabsContent value="weekly" className="space-y-4 mt-4">
          <div className="bg-[#141414] border border-[#262626] rounded-md p-4">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-[#D4AF37]" />
                <div>
                  <p className="text-white font-bold text-sm">Payroll Week (Sat–Fri)</p>
                  <p className="text-[#A3A3A3] text-xs" data-testid="week-range">{week ? `${fmtETDate(`${week.week_start}T12:00:00`)} — ${fmtETDate(`${week.week_end}T12:00:00`)}` : '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid="week-prev" size="sm" variant="outline" onClick={() => shiftWeek(-7)} className="border-[#262626] text-white hover:bg-[#262626]"><ChevronLeft className="w-4 h-4" /></Button>
                <Button data-testid="week-today" size="sm" variant="outline" onClick={() => setWeekAnchor(today)} className="border-[#262626] text-white hover:bg-[#262626]">This Week</Button>
                <Button data-testid="week-next" size="sm" variant="outline" onClick={() => shiftWeek(7)} className="border-[#262626] text-white hover:bg-[#262626]"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="weekly-grid">
              {(week?.days || []).map((d) => {
                const isToday = d.date === today;
                return (
                  <button
                    key={d.date}
                    type="button"
                    data-testid={`week-day-${d.date}`}
                    onClick={() => openDay(d.date)}
                    className={`text-left border rounded-md p-3 transition-colors hover:border-[#D4AF37] ${isToday ? 'border-[#D4AF37] bg-[#D4AF37]/5' : d.is_weekend ? 'border-[#262626] bg-[#0A0A0A]/60' : 'border-[#262626] bg-[#0A0A0A]'}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className={`text-[10px] uppercase tracking-wider font-bold ${isToday ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'}`}>{d.weekday}</p>
                      <p className="text-[#737373] text-xs">{d.day_num}</p>
                    </div>
                    <p className="text-white text-lg font-bold mt-2">{fmtHM(d.paid_minutes)}</p>
                    <p className="text-[10px] text-[#737373]">{d.paid_hours.toFixed(2)}h paid</p>
                    {d.gross_minutes > 0 && <p className="text-[10px] text-[#737373]">{fmtHM(d.gross_minutes)} gross</p>}
                    {d.has_open_entry && <p className="text-[10px] text-[#D4AF37] font-bold mt-1 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" /> On clock</p>}
                    {d.paid_leave && <p className="text-[10px] text-blue-300 mt-1">{d.paid_leave.type_label}: {d.paid_leave.hours}h</p>}
                  </button>
                );
              })}
            </div>

            {/* Weekly hero — PAID is the canonical payroll total (matches Workforce Hub). */}
            <div className="mt-4 pt-3 border-t border-[#262626]">
              <div data-testid="week-total-paid">
                <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Paid This Week</p>
                <p className="text-3xl sm:text-4xl font-bold text-[#D4AF37] leading-none mt-1">{fmtHM(week?.total_paid_minutes)}</p>
                <p className="text-sm text-[#737373] mt-1">{(week?.total_paid_hours ?? 0).toFixed(2)}h payroll</p>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
                <div data-testid="week-gross-clocked">
                  <p className="text-[#737373] text-[10px] uppercase tracking-wider">Gross Clocked</p>
                  <p className="text-base font-semibold text-[#A3A3A3]">{(week?.total_gross_hours ?? 0).toFixed(2)}h</p>
                </div>
                <div data-testid="week-unpaid-breaks">
                  <p className="text-[#737373] text-[10px] uppercase tracking-wider">Unpaid Breaks</p>
                  <p className="text-base font-semibold text-[#A3A3A3]">{(week?.total_break_hours ?? 0) > 0 ? '−' : ''}{(week?.total_break_hours ?? 0).toFixed(2)}h</p>
                </div>
              </div>
            </div>
            {/* iter246 — Separated payroll metrics + bonus visibility */}
            <div className="mt-4 pt-3 border-t border-[#262626]" data-testid="week-payroll-summary">
              <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider mb-2">Payroll Breakdown</p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                <div><p className="text-[9px] text-[#737373] uppercase">Worked</p><p className="text-sm font-bold text-white" data-testid="week-worked-hours">{(week?.worked_paid_hours ?? 0).toFixed(2)}h</p></div>
                <div><p className="text-[9px] text-[#737373] uppercase">PTO</p><p className="text-sm font-bold text-sky-300" data-testid="week-pto-hours">{(week?.total_pto_hours ?? 0).toFixed(2)}h</p></div>
                <div><p className="text-[9px] text-[#737373] uppercase">Sick</p><p className="text-sm font-bold text-rose-300" data-testid="week-sick-hours">{(week?.total_paid_sick_hours ?? 0).toFixed(2)}h</p></div>
                <div><p className="text-[9px] text-[#737373] uppercase">Holiday</p><p className="text-sm font-bold text-violet-300" data-testid="week-holiday-hours">{(week?.total_paid_holiday_hours ?? 0).toFixed(2)}h</p></div>
                <div><p className="text-[9px] text-[#737373] uppercase">Other</p><p className="text-sm font-bold text-amber-300" data-testid="week-other-hours">{(week?.total_other_leave_hours ?? 0).toFixed(2)}h</p></div>
                <div><p className="text-[9px] text-[#737373] uppercase">Total Paid</p><p className="text-sm font-bold text-[#D4AF37]" data-testid="week-grand-total-hours">{(week?.grand_total_paid_hours ?? week?.worked_paid_hours ?? 0).toFixed(2)}h</p></div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-green-500/10 border border-green-500/30 rounded-md p-2 text-center" data-testid="week-approved-bonus">
                  <p className="text-[9px] text-green-300/80 uppercase tracking-wider">Approved Bonuses</p>
                  <p className="text-lg font-bold text-green-300">${(week?.approved_bonus_total ?? 0).toFixed(2)}</p>
                  <p className="text-[9px] text-[#737373]">{week?.approved_bonus_count ?? 0} bonus{(week?.approved_bonus_count ?? 0) === 1 ? '' : 'es'} · in gross pay</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-2 text-center" data-testid="week-pending-bonus">
                  <p className="text-[9px] text-amber-300/80 uppercase tracking-wider">Pending Bonuses</p>
                  <p className="text-lg font-bold text-amber-300">${(week?.pending_bonus_total ?? 0).toFixed(2)}</p>
                  <p className="text-[9px] text-[#737373]">{week?.pending_bonus_count ?? 0} pending · not yet paid</p>
                </div>
              </div>
              {(week?.bonus_items || []).length > 0 && (
                <div className="mt-2 space-y-1" data-testid="week-bonus-list">
                  {week.bonus_items.map((b) => (
                    <div key={b.id} className="flex items-center justify-between text-[11px] bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1">
                      <span className="text-[#C9C9C9] truncate mr-2">{b.customer_name || b.reason || 'Bonus'}{b.work_date ? ` · ${b.work_date}` : ''}</span>
                      <span className={`font-bold whitespace-nowrap ${b.status === 'pending' ? 'text-amber-300' : 'text-green-300'}`}>${(b.amount || 0).toFixed(2)} {b.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[10px] text-[#737373] mt-3">Tap any day to see shift times, breaks and paid time. Clock actions are on the Today tab only.</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Day-detail drawer (Weekly) ── */}
      <Sheet open={!!drawerDate} onOpenChange={(o) => { if (!o) { setDrawerDate(null); setDrawerSheet(null); setDrawerBreaks({}); } }}>
        <SheetContent side="right" className="bg-[#0A0A0A] border-[#262626] w-full sm:max-w-lg overflow-y-auto" data-testid="day-detail-drawer">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#D4AF37]" /> {drawerDate ? fmtETDate(`${drawerDate}T12:00:00`) : ''}
            </SheetTitle>
          </SheetHeader>
          {drawerLoading ? (
            <p className="text-[#A3A3A3] text-sm mt-6">Loading…</p>
          ) : drawerSheet ? (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-3 bg-[#141414] border border-[#262626] rounded-md p-3">
                <div>
                  <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Paid</p>
                  <p className="text-lg font-bold text-[#D4AF37]">{fmtHM(drawerSheet.paid_minutes)}</p>
                  <p className="text-[11px] text-[#737373]">{(drawerSheet.total_hours ?? 0).toFixed(2)}h</p>
                </div>
                <div>
                  <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Gross Clocked</p>
                  <p className="text-lg font-bold text-[#A3A3A3]">{fmtHM(drawerSheet.gross_minutes)}</p>
                </div>
                <div>
                  <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Unpaid Breaks</p>
                  <p className="text-lg font-bold text-[#A3A3A3]">{fmtHM(drawerSheet.break_minutes)}</p>
                </div>
              </div>

              {drawerSheet.paid_leave && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-md p-3 text-sm text-blue-200" data-testid="drawer-paid-leave">
                  {drawerSheet.paid_leave.type_label}: {drawerSheet.paid_leave.hours}h paid leave
                </div>
              )}
              {drawerSheet.paid_holiday && (
                <div className="bg-violet-500/10 border border-violet-500/30 rounded-md p-3 text-sm text-violet-200" data-testid="drawer-paid-holiday">
                  {drawerSheet.paid_holiday.name || 'Paid Holiday'}: {drawerSheet.paid_holiday.hours}h holiday pay
                </div>
              )}
              {(drawerSheet.total_paid_hours ?? 0) > (drawerSheet.total_hours ?? 0) && (
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-md p-3 text-sm text-[#D4AF37] font-bold flex items-center justify-between" data-testid="drawer-total-paid">
                  <span>Total Paid Hours</span><span>{(drawerSheet.total_paid_hours ?? 0).toFixed(2)}h</span>
                </div>
              )}
              {(drawerSheet.bonus_items || []).length > 0 && (
                <div data-testid="drawer-bonuses">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-1">Bonuses (this day)</p>
                  <div className="space-y-1">
                    {drawerSheet.bonus_items.map((b) => (
                      <div key={b.id} className="flex items-center justify-between text-[11px] bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1">
                        <span className="text-[#C9C9C9] truncate mr-2">{b.customer_name || b.reason || 'Bonus'}</span>
                        <span className={`font-bold whitespace-nowrap ${b.status === 'pending' ? 'text-amber-300' : 'text-green-300'}`}>${(b.amount || 0).toFixed(2)} {b.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {drawerSheet.allocation && <TimeAllocation allocation={drawerSheet.allocation} />}

              <div>
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">Shifts & Segments</p>
                <div className="space-y-3">
                  {(drawerSheet.entries || []).length === 0 ? (
                    <p className="text-[#A3A3A3] text-sm">No entries on this day.</p>
                  ) : drawerSheet.entries.map((e) => renderEntry(e, drawerBreaks, setDrawerBreaks, false))}
                </div>
              </div>

              {(drawerSheet.notes || []).length > 0 && (
                <div>
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">Notes</p>
                  <div className="space-y-1">
                    {drawerSheet.notes.map((n) => (
                      <p key={n.id} className="text-[#A3A3A3] text-sm border-l-2 border-[#D4AF37] pl-2">{n.note} <span className="text-[#525252] text-xs">— {fmtTime(n.created_at)}</span></p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* ── After-Hours Bonus dialog ── */}
      <Dialog open={ahOpen} onOpenChange={setAhOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg" data-testid="after-hours-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2"><Zap className="w-5 h-5" /> After-Hours Completed Call Bonus — $100</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-md p-2.5 text-xs text-[#E5D5A0]">
              Fixed <span className="font-bold">$100</span> bonus per completed after-hours / weekend / on-call job. Amount is set by the office; you cannot change it here.
            </div>
            <div>
              <Label className="text-white">Completed job *</Label>
              <Select value={ahSelected} onValueChange={setAhSelected}>
                <SelectTrigger data-testid="ah-job-select" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Select a completed job" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626] max-h-72">
                  {ahCandidates.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-[#A3A3A3]">No completed jobs found in the last 3 weeks.</div>
                  ) : ahCandidates.map((c) => (
                    <SelectItem key={c.appointment_id} value={c.appointment_id} disabled={!!c.already_requested} className="text-white" data-testid={`ah-candidate-${c.appointment_id}`}>
                      {c.date} · {c.customer_name || 'Customer'} {c.already_requested ? '(requested)' : (c.eligible ? '✓' : '· review')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {ahSelectedObj && (
              <div className="border border-[#262626] rounded-md p-3 bg-[#0d0d0d] space-y-2" data-testid="ah-autofill">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><p className="text-[#737373] text-[10px] uppercase">Customer</p><p className="text-white">{ahSelectedObj.customer_name || '—'}</p></div>
                  <div><p className="text-[#737373] text-[10px] uppercase">Service Date</p><p className="text-white">{ahSelectedObj.date}</p></div>
                  <div><p className="text-[#737373] text-[10px] uppercase">Window</p><p className="text-white">{ahSelectedObj.start_time || '—'}–{ahSelectedObj.end_time || '—'}</p></div>
                  <div><p className="text-[#737373] text-[10px] uppercase">Work Order</p><p className="text-white">{ahSelectedObj.work_order_number || '—'}</p></div>
                </div>
                {ahSelectedObj.eligible ? (
                  <div className="flex items-start gap-2 text-xs text-green-300" data-testid="ah-eligible">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Qualifies: {ahSelectedObj.reasons.join(', ')}</span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-xs text-amber-300" data-testid="ah-needs-review">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>We couldn&apos;t auto-confirm this as after-hours. You can still submit it — it will go to the office as <span className="font-bold">Needs Office Review</span>.</span>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-white">Notes (optional)</Label>
              <Textarea data-testid="ah-notes" value={ahNotes} onChange={(e) => setAhNotes(e.target.value)} rows={2} placeholder="Anything the office should know" className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>

            <div className="flex items-center justify-between pt-1">
              <p className="text-[#D4AF37] font-bold text-lg">$100.00</p>
              <Button data-testid="ah-submit" onClick={submitAfterHours} disabled={!ahSelected || ahLoading || !!ahSelectedObj?.already_requested} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
                {ahSelectedObj?.already_requested ? 'Already Requested' : 'Submit $100 Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fallback Clock-In dialog */}
      <Dialog open={fallbackOpen} onOpenChange={setFallbackOpen}>
        <DialogContent data-testid="fallback-dialog" className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader><DialogTitle className="text-[#D4AF37]">Clock In Without Shift</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-[#A3A3A3] text-sm">
              Heads up — clocking in without a scheduled shift flags your entry for admin review. Pick the closest reason so the office knows what happened.
            </p>
            <div>
              <Label className="text-white">Reason *</Label>
              <Select value={fallbackForm.reason} onValueChange={(v) => setFallbackForm({ ...fallbackForm, reason: v })}>
                <SelectTrigger data-testid="fallback-reason-select" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {FALLBACK_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value} className="text-white">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFallbackOpen(false)} className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
            <Button data-testid="fallback-submit" onClick={submitFallback} disabled={loading || !fallbackForm.reason} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              Clock In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time-edit request dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader><DialogTitle className="text-[#D4AF37]">Request Time Edit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white">Reason category</Label>
              <select
                data-testid="edit-reason-category"
                value={editForm.reason_category}
                onChange={(e) => setEditForm({...editForm, reason_category: e.target.value})}
                className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded h-10 px-3 text-sm"
              >
                <option value="">— Select —</option>
                <option value="forgot_clock_in">Forgot to clock in</option>
                <option value="forgot_clock_out">Forgot to clock out</option>
                <option value="wrong_job">Clocked into wrong job</option>
                <option value="break_time_wrong">Break time wrong</option>
                <option value="app_phone_issue">App/phone issue</option>
                <option value="poor_service">Poor service</option>
                <option value="admin_asked">Admin asked me to submit correction</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label className="text-white">What needs correcting?</Label>
              <select
                data-testid="edit-correction-type"
                value={editForm.correction_type || 'both'}
                onChange={(e) => setEditForm({ ...editForm, correction_type: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md h-10 px-3 text-white text-sm"
              >
                <option value="clock_in_only">Correct clock-in time only</option>
                <option value="clock_out_only">Correct clock-out time only</option>
                <option value="both">Correct both clock-in and clock-out</option>
              </select>
            </div>
            {(editForm.correction_type || 'both') !== 'clock_out_only' && (
              <div>
                <Label className="text-white">Corrected Clock-In</Label>
                <Input data-testid="edit-clockin" type="datetime-local" value={editForm.proposed_clock_in} onChange={(e) => setEditForm({...editForm, proposed_clock_in: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            )}
            {(editForm.correction_type || 'both') !== 'clock_in_only' && (
              <div>
                <Label className="text-white">Corrected Clock-Out</Label>
                <Input data-testid="edit-clockout" type="datetime-local" value={editForm.proposed_clock_out} onChange={(e) => setEditForm({...editForm, proposed_clock_out: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" />
                <p className="text-[11px] text-[#737373] mt-1">Still on the clock? Choose “clock-in only” — we won’t clock you out.</p>
              </div>
            )}
            <div>
              <Label className="text-white">Reason *</Label>
              <Textarea data-testid="edit-reason" value={editForm.reason} onChange={(e) => setEditForm({...editForm, reason: e.target.value})} rows={3} className="bg-[#0A0A0A] border-[#262626] text-white" required={editForm.reason_category === 'other'} />
            </div>
            <Button data-testid="edit-submit" onClick={submitEditRequest} disabled={!editForm.reason} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] w-full">Submit Edit Request</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Missing Entry Request */}
      <Dialog open={missingOpen} onOpenChange={setMissingOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white" data-testid="missing-entry-dialog">
          <DialogHeader><DialogTitle className="text-[#D4AF37]">Request Missing Time Entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white">Date *</Label>
              <Input data-testid="missing-date" type="date" value={missingForm.date} onChange={(e) => setMissingForm({...missingForm, date: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white nf-dark-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Clock-In *</Label>
                <Input data-testid="missing-clockin" type="datetime-local" value={missingForm.proposed_clock_in} onChange={(e) => setMissingForm({...missingForm, proposed_clock_in: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div>
                <Label className="text-white">Clock-Out *</Label>
                <Input data-testid="missing-clockout" type="datetime-local" value={missingForm.proposed_clock_out} onChange={(e) => setMissingForm({...missingForm, proposed_clock_out: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            </div>
            <div>
              <Label className="text-white">Reason category</Label>
              <select
                data-testid="missing-reason-category"
                value={missingForm.reason_category}
                onChange={(e) => setMissingForm({...missingForm, reason_category: e.target.value})}
                className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded h-10 px-3 text-sm"
              >
                <option value="">— Select —</option>
                <option value="forgot_clock_in">Forgot to clock in</option>
                <option value="forgot_clock_out">Forgot to clock out</option>
                <option value="app_phone_issue">App/phone issue</option>
                <option value="admin_asked">Admin asked me to submit correction</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label className="text-white">Reason *</Label>
              <Textarea data-testid="missing-reason" value={missingForm.reason} onChange={(e) => setMissingForm({...missingForm, reason: e.target.value})} rows={3} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <Button data-testid="missing-submit" onClick={submitMissingRequest} disabled={!missingForm.date || !missingForm.proposed_clock_in || !missingForm.proposed_clock_out || !missingForm.reason} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] w-full">
              Submit Missing Entry Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unfinished billing warning modal */}
      <UnfinishedBillingModal
        open={unfinishedOpen}
        precheck={unfinishedPrecheck}
        onClose={() => { setUnfinishedOpen(false); setUnfinishedPrecheck(null); }}
        onClockedOut={onUnfinishedClockedOut}
        onFinishBilling={onUnfinishedFinishBilling}
      />
    </div>
  );
};

export default TimeClock;
